import { useEffect, useState, useRef } from "react";
import Header from "../../components/Header/Header";
import styles from "./Dashboard.module.css";
import { fetchClientList } from "../../utils/clientApi";
import type { ClientItem } from "../../types/Client";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import { fetchSosContactList } from "../../utils/clientApi";
import type { SosContact } from "../../types/Client";
import { fetchSurveyCsvBlob } from "../../utils/surveyApi";
import { formatNowForFilename } from "../../utils/timeUtils";
import { saveBlobAsFile, extractFilenameFromContentDisposition, sanitizeFilePart } from "../../utils/fileDownload";
import { sendSosMessage } from "../../utils/clientApi";
import { hasAnySurveyAnswered } from "../../utils/surveyUtils";
import { sendClientMessage } from "../../utils/messageApi";
import { useSosMessageLog } from "../../hooks/useSosMessageLog";



export default function Dashboard() {
  const navigate = useNavigate();
  const [nameQuery, setNameQuery] = useState("");
  const [joinCodeQuery, setJoinCodeQuery] = useState("");

  const [rows, setRows] = useState<ClientItem[]>([]);
  const [page, setPage] = useState(0);     
  const [size, setSize] = useState(20);    
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);

  const [hasWearable, setHasWearable] = useState<boolean | undefined>(undefined);
  const [isRemote, setIsRemote] = useState<boolean | undefined>(undefined);
  const [sort, setSort] = useState<string>("id,DESC");

  const [openSosFor, setOpenSosFor] = useState<number | null>(null);
  const [sosCache, setSosCache] = useState<Record<number, SosContact[]>>({});
  const [sosLoading, setSosLoading] = useState<Record<number, boolean>>({});
  const [sosError, setSosError] = useState<Record<number, string | null>>({});

  const [downloadingId, setDownloadingId] = useState<number | null>(null);

  const [sendingIds, setSendingIds] = useState<Set<number>>(new Set());

  const {
    openMsgLogFor,
    msgLogCache,
    msgLogLoading,
    msgLogError,
    msgLogFilter,
    toggleMsgLogCard,
    applyMsgLogFilter,
    setOpenMsgLogFor,

    msgLogPage,
    msgLogTotalPages,
    prevMsgLogPage,
    nextMsgLogPage,
  } = useSosMessageLog();

  const isFetchingRef = useRef(false);
  const hasShownInitialAlertRef = useRef(false);

  const [msgOpen, setMsgOpen] = useState(false);
  const [msgTargetId, setMsgTargetId] = useState<number | null>(null);
  const [msgTargetName, setMsgTargetName] = useState<string>("");

  const [msgMode, setMsgMode] = useState<"default" | "custom">("default");

  const [msgText, setMsgText] = useState("");
  const maxLen = 30;

  const sanitizeToKoreanNumber = (raw: string) => {
    const disallowed = /[^\uAC00-\uD7A3\u1100-\u11FF\u3130-\u318F0-9\s.,!?~()\-\[\]]/g;
    return raw.replace(disallowed, "");
  };

  const onChangeMsg = (val: string) => {
    const s = sanitizeToKoreanNumber(val);
    setMsgText(s.slice(0, maxLen));
  };

  const openMsgModal = (clientId: number, name: string) => {
    setMsgTargetId(clientId);
    setMsgTargetName(name);
    setMsgMode("default");
    setMsgText("");
    setMsgOpen(true);
  };

  const closeMsgModal = () => {
    setMsgOpen(false);
    setMsgTargetId(null);
    setMsgTargetName("");
    setMsgText("");
  };

  const handleSend = async () => {
    if (!msgTargetId) return;
    try {
      const payload = msgMode === "default" ? null : (msgText.trim() || null);

      if (msgMode === "custom" && !payload) {
        Swal.fire("문자 내용이 없습니다", "한글/숫자 최대 30자로 입력해 주세요.", "warning");
        return;
      }

      const res = await sendClientMessage(msgTargetId, payload);
      if (res.status >= 200 && res.status < 300) {
        Swal.fire("전송되었습니다", "회원에게 메시지를 성공적으로 보냈습니다.", "success");
        closeMsgModal();
      } else {
        Swal.fire("전송에 실패했습니다", "잠시 후 다시 시도해 주세요.", "error");
      }
    } catch (e: any) {
      const msg = e?.response?.data?.message || e?.message || "알 수 없는 오류";
      Swal.fire("전송에 실패했습니다", msg, "error");
    }
  };

  function toActivityLabel(status?: string | number) {
    if (status === undefined || status === null) return "-";
    const s = String(status).toLowerCase();
    if (["low", "저조", "0"].some(k => s.includes(k))) return "저조";
    if (["high", "활발", "2"].some(k => s.includes(k))) return "활발";
    return "보통";
  }

  function toRecentActivity(v?: boolean) {
    if (v === true) return "있음";
    if (v === false) return "없음";
    return "-";
  }

  function digitsOnly(v?: string) {
    return (v ?? "").replace(/\D/g, "");
  }

  function tailWithout010(v?: string) {
    const d = digitsOnly(v);
    return d.startsWith("010") ? d.slice(3) : d;
  }

  function applyClientFilter(list: ClientItem[], nameQ: string, phoneQ: string) {
    const nq = nameQ.trim();
    const pq = digitsOnly(phoneQ.trim());        

    return list.filter((row) => {
      const nameOk = nq ? (row.name ?? "").includes(nq) : true;

      const phoneTail = tailWithout010(row.phone);   
      const phoneOk = pq ? phoneTail.includes(pq) : true;

      return nameOk && phoneOk;
    });
  }

  async function ensureSosLoaded(clientId: number) {

    const res = await fetchSosContactList({ clientId, page: 0, size: 10, sort: "id,ASC" });
    const sorted = [...(res?.content ?? [])].sort((a, b) => {
      if (a.isDefault) return -1;
      if (b.isDefault) return 1;
      return 0;
    });
    setSosCache(prev => ({ ...prev, [clientId]: sorted }));

    if (sosCache[clientId]) return; 
    try {
      setSosLoading(prev => ({ ...prev, [clientId]: true }));
      setSosError(prev => ({ ...prev, [clientId]: null }));
      const res = await fetchSosContactList({ clientId, page: 0, size: 10, sort: "id,ASC" });
      setSosCache(prev => ({ ...prev, [clientId]: res?.content ?? [] }));
    } catch (e: any) {
      const msg = e?.response?.data?.message || e?.message || "조회 실패";
      setSosError(prev => ({ ...prev, [clientId]: msg }));
    } finally {
      setSosLoading(prev => ({ ...prev, [clientId]: false }));
    }
  }

  async function toggleSosCard(clientId: number) {
    if (openSosFor === clientId) {
      setOpenSosFor(null);
      return;
    }
    setOpenSosFor(clientId);
    await ensureSosLoaded(clientId);
  }


  const handleSearch = () => {
    setPage(0);
    load({ showAlert: false });
  };

  const handleNameFocus = () => {
    if (joinCodeQuery !== "") setJoinCodeQuery("");
  };

  const handlePhoneFocus = () => {
    if (nameQuery !== "") setNameQuery("");
  };

  async function handleDownloadSurveyCsv(clientId: number, clientName: string) {
    setDownloadingId(clientId);
    try {
      const res = await fetchSurveyCsvBlob(clientId);

      const timestamp = formatNowForFilename();

      const safeClientName = sanitizeFilePart(clientName);

      const fallbackName = `${clientId}_${clientName}_설문데이터(${timestamp}).csv`;

      const name = `${clientId}_${safeClientName}_설문데이터(${timestamp}).csv` || fallbackName;

      saveBlobAsFile(res.data, name);

      Swal.fire({
        icon: "success",
        title: "다운로드 시작",
        text: `${name} 파일을 저장합니다.`,
      });
    } catch (e: any) {
      const msg =
        e?.response?.data
          ? typeof e.response.data === "string"
            ? e.response.data
            : "다운로드에 실패했습니다."
          : e?.message ?? "알 수 없는 오류";
      Swal.fire({
        icon: "error",
        title: "다운로드 실패",
        text: msg,
      });
    } finally {
      setDownloadingId(null);
    }
  }

  const handleSendSos = async (id: number) => {
    const confirm = await Swal.fire({
      icon: "question",
      title: "SOS 메시지 전송",
      text: "해당 사용자의 등록된 비상연락처 전체로 메시지를 보낼까요?",
      showCancelButton: true,
      confirmButtonText: "보내기",
      cancelButtonText: "취소",
    });

    if (!confirm.isConfirmed) return;

    setSendingIds(prev => new Set(prev).add(id));
    try {
      await sendSosMessage(id);
      await Swal.fire({
        icon: "success",
        title: "전송 완료",
        text: "비상연락처 목록으로 SOS 메시지를 전송했습니다.",
      });
    } catch (e: any) {
      await Swal.fire({
        icon: "error",
        title: "전송 실패",
        text: e?.message ?? "전송 중 오류가 발생했습니다.",
      });
    } finally {
      setSendingIds(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };


  const load = async (opts: { showAlert?: boolean } = { showAlert: false }) => {

    if (isFetchingRef.current) return;   
    isFetchingRef.current = true;
    setLoading(true);

    try {
      const res = await fetchClientList({
        page,
        size,
        sort,
        hasWearable,
        isRemote,
      });

      const list = res.content ?? [];
      const useFiltered = (nameQuery.trim() !== "" || joinCodeQuery.trim() !== "");
      const filtered = applyClientFilter(list, nameQuery, joinCodeQuery);

      setRows(useFiltered ? filtered : list);

      const total = useFiltered
        ? filtered.length
        : (typeof res?.totalElements === "number" ? res.totalElements : list.length);

      if (useFiltered) {
        setTotalPages(1);
        if (page !== 0) setPage(0);
      } else {
        setTotalPages(res.totalPages > 0 ? res.totalPages : 1);
      }

      if (opts.showAlert) {
        Swal.fire({
          icon: total > 0 ? "success" : "info",
          title: total > 0 ? "목록 불러오기 완료" : "데이터가 없습니다",
          text: total > 0 ? `총 ${total}건을 불러왔습니다.` : "조건에 해당하는 데이터가 없습니다.",
          timer: 1200,
          showConfirmButton: false,
        });
      }
    } catch (e: any) {
      const serverMsg =
        e?.response?.data?.message || e?.response?.data?.error || e?.message;
      Swal.fire({
        icon: "error",
        title: "목록 불러오기 실패",
        text: serverMsg ?? "잠시 후 다시 시도해 주세요.",
      });
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  };

  useEffect(() => {
    if (!hasShownInitialAlertRef.current) {
      hasShownInitialAlertRef.current = true;

      const suppressOnce = sessionStorage.getItem("suppressDashboardAlertOnce") === "1";
      if (suppressOnce) {
        sessionStorage.removeItem("suppressDashboardAlertOnce");
        load({ showAlert: false });
      } else {
        load({ showAlert: true });
      }
    } else {
      load({ showAlert: false });
    }
  }, [page, size, sort, hasWearable, isRemote]);


  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      const popEl = document.querySelector(`.${styles.popCard}`);
      if (openSosFor && popEl && !popEl.contains(e.target as Node)) {
        setOpenSosFor(null);
      }
      if (openMsgLogFor && popEl && !popEl.contains(e.target as Node)) {
        setOpenMsgLogFor(null);
      }
    }
    function handleEsc(e: KeyboardEvent) {
      const isEsc =
        e.key === "Escape" || (e as any).code === "Escape" || (e as any).keyCode === 27;
      if (!isEsc) return;

      if (msgOpen) {
        e.preventDefault();
        closeMsgModal();
        return;
      }

      if (openMsgLogFor !== null) {
        e.preventDefault();
        setOpenMsgLogFor(null);
        return;
      }

      if (openSosFor !== null) {
        e.preventDefault();
        setOpenSosFor(null);
      }
    }
    function handleScroll() {
      if (openSosFor) setOpenSosFor(null);
      if (openMsgLogFor) setOpenMsgLogFor(null);
    }

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEsc);
    window.addEventListener("scroll", handleScroll);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEsc);
      window.removeEventListener("scroll", handleScroll);
    };
  }, [openSosFor, msgOpen, openMsgLogFor]);

  function handleOpenSurveyResult(clientId: number, name?: string) {
    sessionStorage.setItem("suppressDashboardAlertOnce", "1");
    navigate(`/survey-result/${clientId}`, { state: { name } });
  }
    
  return (
    <div className={styles.wrap}>
      <Header />

      <main className={styles.main}>
        <div className={styles.toolbar}>
          <div className={styles.row}>
            <div className={styles.leftControls}>
              <div className={styles.field}>
                <label>페이지당 행수:</label>
                <select
                  value={size}
                  onChange={(e) => { setSize(Number(e.target.value)); setPage(0); }}
                >
                  {[10, 20, 30, 50].map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>

              <div className={styles.field}>
                <label>정렬:</label>
                <select
                  value={sort}
                  onChange={(e) => setSort(e.target.value)}
                >
                  <option value="id,DESC">계정생성 내림차순</option>
                  <option value="id,ASC">계정생성 오름차순</option>
                </select>
              </div>
            </div>

            <h2 className={styles.title}>회원 목록</h2>

            <div className={styles.searches}>
              <div className={styles.searchBox}>
                <label>이름:</label>
                <input
                  value={nameQuery}
                  onChange={(e) => setNameQuery(e.target.value)}
                  placeholder="이름 검색"
                  onFocus={handleNameFocus}  
                  onKeyDown={(e) => { if (e.key === "Enter") handleSearch(); }} 
                />
              </div>
              <div className={styles.searchBox}>
                <label>전화번호:</label>
                <input
                  value={joinCodeQuery}
                  onChange={(e) => setJoinCodeQuery(e.target.value)}
                  placeholder="전화번호 검색"
                  onFocus={handlePhoneFocus}
                  onKeyDown={(e) => { if (e.key === "Enter") handleSearch(); }}
                />
              </div>
              <button className={styles.searchBtn} onClick={handleSearch}>검색</button>
              <button
                className={styles.searchBtn}
                type="button"
                onClick={() => window.location.reload()}
                aria-label="검색 조건 초기화"
                title="검색 조건 초기화"
              >
                초기화
              </button>
            </div>
          </div>
        </div>

        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr className={styles.theadRow1}>
                <th rowSpan={2} className={`${styles.stickyColTH} ${styles.colId}`}>고유 번호</th>
                <th rowSpan={2} className={`${styles.stickyColTH} ${styles.colName}`}>이름</th>
                <th rowSpan={2} className={`${styles.stickyColTH} ${styles.colGender}`}>성별</th>
                <th rowSpan={2} className={`${styles.stickyColTH} ${styles.colBirth}`}>생년월일</th>
                <th rowSpan={2} className={`${styles.stickyColTH} ${styles.colPhone}`}>전화번호</th>
                <th rowSpan={2}>스마트폰 정보</th>
                <th rowSpan={2}>비상연락처</th>
                <th rowSpan={2} className={styles.wideHeader}>
                  최근 12시간<br />활동 여부
                </th>
                <th rowSpan={2}>SOS 메시지 전송</th>
                <th rowSpan={2}>회원 문자 전송</th>
                <th rowSpan={2}>문자 전송 내역</th>
                <th colSpan={8} className={styles.surveyCell}>설문 조사 여부</th>
                <th rowSpan={2}>설문 결과</th>
                <th rowSpan={2}>설문 데이터 다운</th>
              </tr>
              <tr className={styles.theadRow2}>
                <th>인구학적 특성</th>
                <th>건강상태</th>
                <th>경제상황</th>
                <th>주거환경</th>
                <th>일상생활</th>
                <th>심리정서</th>
                <th>사회적 관계망</th>
                <th>욕구</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={21} style={{ textAlign: "center" }}>데이터가 없습니다</td>
                </tr>
              ) : (
                rows.map((row, idx) => {
                  const done = (keyword: string) =>
                    row.surveyHistoryList?.some(s => (s.title || "").includes(keyword) && s.completed);

                  return (
                    <tr key={row.id}>
                      <td className={`${styles.stickyCol} ${styles.colId}`}>{row.id}</td>
                      <td className={`${styles.stickyCol} ${styles.colName}`}>{row.name}</td>
                      <td className={`${styles.stickyCol} ${styles.colGender}`}>{row.gender ?? "-"}</td>
                      <td className={`${styles.stickyCol} ${styles.colBirth}`}>{row.birthday ?? "-"}</td>
                      <td className={`${styles.stickyCol} ${styles.colPhone}`}>{row.phone ?? "-"}</td>
                      <td>{row.deviceName ?? "-"}</td>
                      <td className={`${styles.popCell} ${!row.hasSosContact ? styles.unregistered : ""}`}>
                        {row.hasSosContact ? (
                          <button
                            type="button"
                            className={styles.linkBtn}     
                            onClick={() => toggleSosCard(row.id)}
                          >
                            등록 완료
                          </button>
                        ) : (
                          "미등록"
                        )}

                        {openSosFor === row.id && (
                            <div className={styles.popCard}>
                              <button
                                type="button"
                                className={styles.popClose}
                                onClick={() => setOpenSosFor(null)}
                                aria-label="닫기"
                              >
                                ✕
                              </button>
                            {sosLoading[row.id] && <div className={styles.popItem}>조회중…</div>}
                            {sosError[row.id] && <div className={styles.popError}>{sosError[row.id]}</div>}
                            {!sosLoading[row.id] && !sosError[row.id] && (sosCache[row.id]?.length ?? 0) === 0 && (
                              <div className={styles.popItem}>등록된 비상연락처 없음</div>
                            )}
                            {!sosLoading[row.id] && (sosCache[row.id]?.length ?? 0) > 0 && (
                              <ul className={styles.popList}>
                                {sosCache[row.id]!.map(c => (
                                  <li key={c.id} className={styles.popItem}>
                                    <strong>{c.name || "데이터 없음"}</strong>
                                    {c.memo && <span style={{ marginLeft: 6, color: "#6b7280" }}>({c.memo})</span>}
                                    <div>{c.phone || "-"}</div>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        )}
                      </td>
                      <td className={!row.isActiveIn12h ? styles.recentInactive : undefined}>
                        {!row.isActiveIn12h && <span className={styles.blinkIcon}>⚠</span>}{" "}
                        {toRecentActivity(row.isActiveIn12h)}
                      </td>
                      <td>
                        <button
                          type="button"
                          className={styles.actionBtn}
                          onClick={() => handleSendSos(row.id)}
                          disabled={!row.hasSosContact || sendingIds.has(row.id)} 
                        >
                          {sendingIds.has(row.id) ? "전송 중..." : "보내기"}
                        </button>
                      </td>
                      <td>
                        <button
                          type="button"
                          className={styles.actionBtn}
                          onClick={() => openMsgModal(row.id, row.name)}
                        >
                          문자 보내기
                        </button>
                      </td>
                      <td className={styles.popCell}>
                        <button
                          type="button"
                          className={styles.actionBtn}
                          onClick={() => toggleMsgLogCard(row.id)}
                        >
                          조회
                        </button>

                        {openMsgLogFor === row.id ? (
                          <div className={`${styles.popCard} ${styles.msgLogCard}`}>
                            <button
                              type="button"
                              className={styles.popClose}
                              onClick={() => setOpenMsgLogFor(null)}
                              aria-label="닫기"
                            >
                              ✕
                            </button>

                            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                              <label style={{ color: "#4b5563", fontSize: 13 }}>수신 구분:</label>
                              <select
                                value={msgLogFilter[row.id] ?? ""}
                                onChange={(e) => applyMsgLogFilter(row.id, e.target.value)}
                                style={{ height: 28, borderRadius: 6, border: "1px solid #e5e7eb", padding: "0 8px" }}
                              >
                                <option value="">전체</option>
                                <option value="ADMIN">관리자</option>
                                <option value="CONTACT">비상연락망</option>
                                <option value="CLIENT">회원</option>
                              </select>
                              {(() => {
                                const cur = msgLogPage[row.id] ?? 0;
                                const total = msgLogTotalPages[row.id] ?? 0;
                                const loading = !!msgLogLoading[row.id];
                                if (total <= 0) return null;
                                return (
                                  <div className={styles.msgPagerTop}>
                                    <button
                                      type="button"
                                      className={styles.actionBtn}
                                      onClick={() => prevMsgLogPage(row.id)}
                                      disabled={cur <= 0 || loading}
                                    >
                                      이전
                                    </button>

                                    <span className={styles.msgPage}>{cur + 1} / {total}</span>

                                    <button
                                      type="button"
                                      className={styles.actionBtn}
                                      onClick={() => nextMsgLogPage(row.id)}
                                      disabled={cur >= total - 1 || loading}
                                    >
                                      다음
                                    </button>
                                  </div>
                                );
                              })()}
                            </div>

                            {(() => {
                              const loading = !!msgLogLoading[row.id];
                              const error = msgLogError[row.id];
                              const items = msgLogCache[row.id] ?? [];
                              const cur = msgLogPage[row.id] ?? 0;
                              const total = msgLogTotalPages[row.id] ?? 0;

                              if (loading) return <div className={styles.popItem}>조회중…</div>;
                              if (error) return <div className={styles.popError}>{error}</div>;
                              if (items.length === 0) return <div className={styles.popItem}>전송 내역이 없습니다</div>;

                              return (
                                <>
                                  <ul className={styles.popList}>
                                    {items.map((m) => {
                                      // 현재 조회 중인 사용자의 실제 이름 찾기
                                      const currentUser = rows.find(r => r.id === row.id);
                                      const actualUserName = currentUser?.name || "알 수 없음";
                                      
                                      return (
                                        <li key={m.id} className={styles.popItem}>
                                          <div style={{ fontWeight: 600 }}>
                                            {m.recipientType?.serverName === "ADMIN" &&
                                              `${actualUserName} → 관리자 (앱 자동 발송)`}
                                            {m.recipientType?.serverName === "CONTACT" &&
                                              `관리자 → [비상연락망] ${m.name ?? "-"}`}
                                            {m.recipientType?.serverName === "CLIENT" &&
                                              `관리자 → [회원] ${actualUserName}`}
                                          </div>

                                          <div style={{ marginTop: 2 }} className={styles.msgLogText}>
                                            {m.content ?? "-"}
                                          </div>

                                          <div style={{ marginTop: 2, color: "#6b7280", fontSize: 12 }}>
                                            {m.createdAt}
                                          </div>
                                        </li>
                                      );
                                    })}
                                  </ul>
                                </>
                              );
                            })()}
                          </div>
                        ) : null}
                      </td>


                      <td>{done("인구학") ? "O" : "X"}</td>
                      <td>{done("건강") ? "O" : "X"}</td>
                      <td>{done("경제") ? "O" : "X"}</td>
                      <td>{done("주거") ? "O" : "X"}</td>
                      <td>{done("일상") ? "O" : "X"}</td>
                      <td>{done("심리") ? "O" : "X"}</td>
                      <td>{done("사회") ? "O" : "X"}</td>
                      <td>{done("욕구") ? "O" : "X"}</td>
                      <td>
                        <button
                          className={styles.actionBtn}
                          onClick={() => handleOpenSurveyResult(row.id, row.name)}
                          disabled={!hasAnySurveyAnswered(row)}
                        >
                          보기
                        </button>
                      </td>
                      <td>
                        <button
                          className={styles.actionBtn}
                          onClick={() => handleDownloadSurveyCsv(row.id, row.name)}
                          disabled={!hasAnySurveyAnswered(row) || downloadingId === row.id || loading} 
                          aria-busy={downloadingId === row.id}
                        >
                          {downloadingId === row.id ? "다운중..." : "다운로드"}
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div className={styles.pagingFixed}>
          <div className={styles.pgBar}>
            <div className={styles.pgBtns}>
              <button
                type="button"
                className={styles.pgBtn}
                disabled={page <= 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
              >
                이전
              </button>

              <span className={styles.pgPage}>
                {page + 1} / {totalPages}
              </span>

              <button
                type="button"
                className={styles.pgBtn}
                disabled={page + 1 >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              >
                다음
              </button>
            </div>
          </div>
        </div>
        <div style={{ height: 72 }} />
        {msgOpen && (
          <div className={styles.msgBackdrop} onClick={closeMsgModal}>
            <div className={styles.msgCard} onClick={(e) => e.stopPropagation()}>
              <div className={styles.msgHeader}>
                <div>문자 보내기 · {msgTargetName}</div>
                <button
                  className={styles.msgClose}
                  onClick={closeMsgModal}
                  aria-label="닫기"
                  type="button"
                >
                  ×
                </button>
              </div>

              <div className={styles.msgTabs}>
                <div
                  className={`${styles.msgTab} ${msgMode === "default" ? styles.msgTabActive : ""}`}
                  onClick={() => setMsgMode("default")}
                >
                  기본문자 보내기
                </div>
                <div
                  className={`${styles.msgTab} ${msgMode === "custom" ? styles.msgTabActive : ""}`}
                  onClick={() => setMsgMode("custom")}
                >
                  직접 입력
                </div>
              </div>

              {msgMode === "custom" ? (
                <>
                  <textarea
                    className={styles.msgTextarea}
                    value={msgText}
                    onChange={(e) => onChangeMsg(e.target.value)}
                    placeholder="한글/숫자만 입력 가능, 최대 30자"
                  />
                  <div className={styles.msgCounter}>
                    {msgText.length}/30
                  </div>
                </>
              ) : (
                <p style={{ margin: "6px 2px 0", color: "#374151", fontSize: 14 }}>
                  등록되어 있는 기본문자를 전송합니다.
                </p>
              )}

              <div className={styles.msgActions}>
                <button
                  className={`${styles.msgBtn} ${styles.msgBtnPrimary}`}
                  type="button"
                  onClick={handleSend}
                >
                  보내기
                </button>
                <button
                  className={`${styles.msgBtn} ${styles.msgBtnPrimary}`}
                  type="button"
                  onClick={closeMsgModal}
                >
                  취소
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}